import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { isArray } from "lodash";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { getAPI } from "../api/callAPI";

export default function ImageGallery({ storeID }) {
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(null);
  const [data, setData] = useState(null);

  const [isModalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { endpoint } = useSelector((state) => state.auth);

  const getImageGallery = useCallback(async () => {
    if (storeID) {
      setLoading(true);
      setError(null);
      try {
        const resp = await getAPI(endpoint, `image-gallery/${storeID}`);
        if (resp.success) {
          if (isArray(resp.data) && resp.data.length > 0) {
            const images = resp.data.map((image) => ({
              original: image?.image || "",
              thumbnail: image?.thumb_image || "",
            }));
            setData(images);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("getImageGallery error >>> ", error);
        setData([]);
        setError("No Images");
        setLoading(false);
      }
    }
  }, [endpoint, storeID]);

  useEffect(() => {
    getImageGallery();
  }, [getImageGallery]);

  const handleOpenModal = (index) => {
    setActiveIndex(index);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  if (isLoading) {
    return (
      <Box>
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  if (isError || !isArray(data) || data?.length === 0) {
    return null;
  }

  return (
    <>
      <Typography variant="h5" className="subtitle-main mt-4">
        Our Gallery
      </Typography>
      <Box mt={2} width="100%" className="event-slider gallery-slider">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={20}
          slidesPerView={4}
          pagination={{ clickable: true }}
          navigation
          breakpoints={{
            0: { slidesPerView: 1.5 },
            600: { slidesPerView: 2 },
            960: { slidesPerView: 2 },
            1280: { slidesPerView: 2 },
          }}
        >
          {data.map((item, index) => (
            <SwiperSlide key={index}>
              <img
                src={item.original}
                className="swiper-lazy w-100"
                alt={`Slide ${index + 1}`}
                style={{ cursor: "pointer" }}
                onClick={() => handleOpenModal(index)}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Modal Lightbox */}
        <Modal open={isModalOpen} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "rgba(0,0,0,0.9)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1300,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: "white",
                zIndex: 1400,
              }}
            >
              <CloseIcon />
            </IconButton>

            <Swiper
              initialSlide={activeIndex}
              navigation
              modules={[Navigation]}
              style={{ width: "90%", height: "90%" }}
            >
              {data.map((img, index) => (
                <SwiperSlide key={index}>
                  <Box
                    component="img"
                    src={img.original}
                    alt={`Slide ${index + 1}`}
                    sx={{
                      maxHeight: "80vh",
                      maxWidth: "100%",
                      objectFit: "contain",
                      margin: "auto",
                      display: "block",
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Modal>
      </Box>
    </>
  );
}
